import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Download, Search } from "lucide-react";
import { fmtINR } from "../../lib/constants";

export default function PersonalCatalogAdmin() {
  const [products, setProducts] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", notes: "" });
  const [q, setQ] = useState("");

  const load = () => api.get("/api/crm/salesperson/catalogs").then((r) => setCatalogs(r.data || []));
  useEffect(() => {
    load();
    api.get("/api/products?limit=200").then((r) => setProducts(r.data.items || []));
  }, []);

  const filteredProducts = q
    ? products.filter((p) => (p.name + p.category + p.id + (p.huid || "") + (p.barcode || "")).toLowerCase().includes(q.toLowerCase()))
    : products;

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const create = async (e) => {
    e.preventDefault();
    if (selected.size === 0) { toast.error("Pick at least one product"); return; }
    try {
      const { data } = await api.post("/api/crm/salesperson/catalog", { ...form, product_ids: Array.from(selected) });
      toast.success("Catalog created");
      setSelected(new Set()); setForm({ customer_name: "", customer_phone: "", notes: "" }); setShowForm(false); load();
      // Auto-download PDF
      window.open(`${API_BASE}/crm/salesperson/catalog/${data.id}/pdf`, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = async (id) => {
    const t = localStorage.getItem("sgpl_token");
    const resp = await fetch(`${API_BASE}/crm/salesperson/catalog/${id}/pdf`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `SGPL-${id.slice(0,8)}.pdf`; a.click();
  };

  return (
    <div className="p-6 lg:p-8 grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <div><div className="eyebrow">Walk-in tool</div><h1 className="font-display text-3xl text-maroon">Personal Catalog Builder</h1></div>
          <button onClick={() => setShowForm(true)} disabled={selected.size === 0} className="btn-maroon" data-testid="generate-catalog-btn"><Plus size={14}/> Generate PDF ({selected.size})</button>
        </div>
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-3 text-ash"/>
          <input placeholder="Search product / ID / HUID / Barcode" value={q} onChange={(e) => setQ(e.target.value)} className="w-full border border-line pl-9 pr-3 py-2 bg-white text-sm" data-testid="pc-product-search"/>
        </div>
        <div className="bg-white border border-line max-h-[60vh] overflow-y-auto">
          {filteredProducts.map((p) => (
            <label key={p.id} className={`flex items-center gap-3 p-3 border-b border-line/60 cursor-pointer ${selected.has(p.id) ? "bg-pearl" : ""}`} data-testid={`pc-product-${p.id}`}>
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}/>
              <img src={p.images?.[0]} alt="" className="w-10 h-12 object-cover bg-pearl"/>
              <div className="flex-1">
                <div className="text-sm font-medium">{p.name}</div>
                <div className="text-[10px] text-ash">{p.category} · {p.purity} · {p.weight_grams}g {p.huid ? `· HUID ${p.huid}` : ""} {p.barcode ? `· Barcode ${p.barcode}` : ""}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="eyebrow mb-3">Past Catalogs</div>
        <div className="space-y-2">
          {catalogs.map((c) => (
            <div key={c.id} className="bg-white border border-line p-4" data-testid={`catalog-${c.id}`}>
              <div className="font-medium text-sm">{c.customer_name}</div>
              <div className="text-[10px] text-ash">{c.customer_phone} · {new Date(c.created_at).toLocaleDateString()}</div>
              <div className="text-xs mt-1">{c.products?.length || 0} pieces · <span className="text-maroon font-semibold">{fmtINR(c.total_value || 0)}</span></div>
              <button onClick={() => downloadPdf(c.id)} className="text-xs text-maroon underline mt-2 flex items-center gap-1" data-testid={`download-${c.id}`}><Download size={11}/> PDF</button>
            </div>
          ))}
          {catalogs.length === 0 && <div className="text-xs text-ash">No catalogs yet.</div>}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <form onSubmit={create} className="bg-white border border-line p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><div className="font-display text-2xl text-maroon">Customer details</div><button type="button" onClick={() => setShowForm(false)}><X size={18}/></button></div>
            <input required placeholder="Customer name" className="w-full border border-line px-3 py-2 bg-pearl" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} data-testid="pc-cust-name"/>
            <input required placeholder="Customer phone" className="w-full border border-line px-3 py-2 bg-pearl" value={form.customer_phone} onChange={(e) => setForm({...form, customer_phone: e.target.value})} data-testid="pc-cust-phone"/>
            <textarea placeholder="Notes" rows={3} className="w-full border border-line px-3 py-2 bg-pearl" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
            <button className="btn-maroon w-full justify-center" data-testid="pc-submit">Generate Catalog PDF</button>
          </form>
        </div>
      )}
    </div>
  );
}
